/**
 * Live API → view DTO mappers (the one place raw P1/P4/P5 rows become core/types.ts shapes).
 * core/types.ts is the view contract; the API is the source of truth for values. Where the API has no field yet we
 * fill a neutral default here (listed in the P2 live-wiring report) so views never see a raw row.
 */
import { APP, ApiError, type Http } from '../http';
import { ORDER_STATUSES, CANCELLED_STATUSES } from '@shared/job_status.mjs';
import { isTerminal, phaseOf, type OrderStatus } from '../job_status';
import { initials as initialsOf } from '../format';
import { statusOf } from '../secondStep';
import type {
  BookingRequest, Call, CatalogItem, Customer, Estimate, Job, JobEvent, JobLine, Lead, Me, Page, Tech, TechLive, TranscriptTurn, Vehicle,
} from '../types';

type Row = Record<string, any>;
const str = (v: unknown): string | null => (v == null || v === '' ? null : String(v));
const num = (v: unknown): number | null => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const int = (v: unknown, d = 0) => num(v) ?? d;
const iso = (v: unknown): string | null => (v == null ? null : new Date(v as string).toISOString());
const arr = <T = Row>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);

/** A UI action whose server route has not shipped yet. Views toast it (shell maps `not_available` → "Rolling out"). */
export class NotAvailable extends ApiError {
  constructor(what: string) { super(501, 'not_available', null, { what }); }
}
export const notAvailable = (what: string): Promise<never> => Promise.reject(new NotAvailable(what));
/** 404/405 from a route whose module is not mounted yet (the API answers `{error:'not_found'}`). */
export const isMissingRoute = (e: unknown) => e instanceof ApiError && (e.status === 404 || e.status === 405);
/** `.catch(orRollingOut('x'))` on a call whose route has not shipped: 404/405 → `not_available` ("Rolling out"), rest rethrown. */
export const orRollingOut = (what: string) => (e: unknown): never => { throw isMissingRoute(e) ? new NotAvailable(what) : e; };

/** `{items,next}` | `{items,next_after}` | `{<key>:[...]}` | `[...]` → Page. */
export function toPage<T>(r: unknown, map: (x: Row) => T, key = 'items'): Page<T> {
  const o = (r ?? {}) as Row;
  const rows = Array.isArray(r) ? r : arr(o[key] ?? o.items);
  return { items: rows.map((x) => map(x as Row)), next: str(o.next ?? o.next_after), ...(o.total != null ? { total: Number(o.total) } : {}) };
}
export const toList = <T>(r: unknown, map: (x: Row) => T, key = 'items'): T[] => toPage(r, map, key).items;

// ─────────────────────────────────────────────────────────── session
const TENANT_STATUS = ['provisioning', 'onboarding', 'live', 'suspended', 'cancelled'] as const;
export function apiMe(r: Row): Me {
  const t = (r.tenant ?? {}) as Row;
  const u = (r.user ?? {}) as Row;
  return {
    user: { id: u.id ?? '', email: u.email ?? '', first_name: u.first_name ?? '', last_name: u.last_name ?? '', role: u.role ?? 'staff', phone_e164: str(u.phone_e164) },
    tenant: {
      id: t.id, name: t.name ?? '', slug: t.slug ?? '',
      status: (TENANT_STATUS as readonly string[]).includes(t.status) ? t.status : 'onboarding',
      timezone: t.timezone ?? t.default_timezone ?? 'America/Chicago', currency: 'usd', go_live_date: str(t.go_live_date),
      segment: str(t.segment), settings: (t.settings && typeof t.settings === 'object' ? t.settings : {}) as Record<string, unknown>,
    },
    tenants: arr(r.tenants).map((x) => ({ id: x.id, name: x.name })),
    locations: arr(r.locations).map((l) => ({ id: l.id, name: l.name, timezone: l.timezone ?? t.timezone ?? 'America/Chicago', lat: num(l.lat), lng: num(l.lng), is_default: !!l.is_default,
      address: l.address && typeof l.address === 'object' ? l.address : null, phone_e164: str(l.phone_e164) })),
    permissions: arr<string>(r.permissions),
    entitlements: arr<string>(r.entitlements),
    flags: (r.flags ?? {}) as Record<string, boolean>,
    csrf: str(r.csrf) ?? '1',
    mfa: statusOf(r.mfa),
  };
}

// ─────────────────────────────────────────────────────────── people & things
export const vehicleLabel = (v: Row | null | undefined) =>
  v ? [v.year, v.make, v.model].filter(Boolean).join(' ') || v.unit_no || v.plate || 'Vehicle' : null;

export function apiVehicle(v: Row): Vehicle {
  return {
    id: v.id, customer_id: v.customer_id, vin: str(v.vin), year: num(v.year), make: str(v.make), model: str(v.model), trim: str(v.trim),
    fuel: ['gas', 'diesel', 'hybrid', 'ev'].includes(v.fuel) ? v.fuel : null, plate: str(v.plate), plate_state: str(v.plate_state),
    color: str(v.color), unit_no: str(v.unit_no), odometer_mi: num(v.odometer_mi), label: vehicleLabel(v) ?? 'Vehicle',
  };
}

/** Latest SMS consent per address (ops.contact_consents rows, newest wins). */
function smsConsent(c: Row): Customer['sms_consent'] {
  const rows = arr(c.consents).filter((x) => x.channel === 'sms' && (!c.phone_e164 || x.address === c.phone_e164))
    .sort((a, b) => String(b.at).localeCompare(String(a.at)));
  return rows[0] ? (rows[0].consent ? 'granted' : 'revoked') : 'unknown';
}
export function apiCustomer(c: Row): Customer {
  return {
    id: c.id, kind: c.kind === 'business' ? 'business' : 'person', display_name: c.display_name ?? '', first_name: str(c.first_name), last_name: str(c.last_name),
    company: str(c.company), phone_e164: str(c.phone_e164), email: str(c.email), tags: arr<string>(c.tags), source: str(c.source), notes: str(c.notes),
    sms_consent: smsConsent(c), created_at: iso(c.created_at) ?? '',
    addresses: arr(c.addresses).map((a, i) => ({
      id: a.id, label: str(a.label), line1: a.line1 ?? '', line2: str(a.line2), city: a.city ?? '', state: a.state ?? '', zip: str(a.zip),
      lat: num(a.lat), lng: num(a.lng), access_notes: str(a.access_notes), is_default: a.is_default ?? i === 0,
    })),
    vehicle_ids: arr(c.vehicles).map((v) => v.id),
  };
}

const TECH_COLORS = ['#F5A524', '#8B7FFF', '#22C55E', '#38BDF8', '#F472B6', '#FB923C', '#A3E635', '#E879F9'];
const colorFor = (id: string) => TECH_COLORS[[...id].reduce((h, ch) => (h * 31 + ch.charCodeAt(0)) >>> 0, 7) % TECH_COLORS.length]!;
export function apiTech(t: Row): Tech {
  const first = t.first_name ?? ''; const last = t.last_name ?? '';
  const name = t.name ?? ([first, last].filter(Boolean).join(' ') || 'Technician');
  return {
    id: t.id, user_id: str(t.user_id), first_name: first || name, last_name: last, display_name: name, initials: initialsOf(name), color: t.color ?? colorFor(String(t.id)),
    phone_e164: str(t.phone_e164), email: str(t.email), location_id: str(t.location_id), skills: arr<string>(t.skills),
    certifications: arr(t.certifications).map((c) => (typeof c === 'string' ? c : String(c?.name ?? c?.key ?? ''))).filter(Boolean),
    van_label: str(t.van_label), van_model: str(t.van_model), home_lat: num(t.home_lat), home_lng: num(t.home_lng),
    pay_type: ['hourly', 'per_job', 'commission'].includes(t.pay_type) ? t.pay_type : null, pay_rate: num(t.pay_rate), max_jobs_per_day: num(t.max_jobs_per_day),
    active: t.active !== false && t.user_status !== 'disabled',
    app_status: t.user_status === 'active' ? 'active' : t.user_status === 'invited' ? 'invited' : 'not_invited',
    rating: num(t.rating), years_experience: num(t.years_experience),
  };
}

/** No `/techs/live` yet: derive each tech's state from their current job (P3 GPS arrives over SSE `tech.location`). */
export function deriveTechLive(techs: Tech[], jobs: Job[]): TechLive[] {
  const RANK: Record<string, number> = { in_progress: 4, arrived: 3, en_route: 2, assigned: 1 };
  return techs.map((t) => {
    const cur = jobs.filter((j) => j.assigned_tech_id === t.id && RANK[phaseOf(j.order_status)])
      .sort((a, b) => RANK[phaseOf(b.order_status)]! - RANK[phaseOf(a.order_status)]!)[0];
    const ph = cur ? phaseOf(cur.order_status) : null;
    const state: TechLive['state'] = !t.active ? 'off' : ph === 'en_route' ? 'enroute' : ph === 'arrived' || ph === 'in_progress' ? 'onsite' : 'available';
    return { tech_id: t.id, state, job_id: ph && ph !== 'assigned' ? cur!.id : null, eta_min: null, lat: t.home_lat, lng: t.home_lng, heading: null, at: null, alert_min: null };
  });
}

export function apiCatalogItem(c: Row): CatalogItem {
  return {
    id: c.id, sku: str(c.sku), name: c.name ?? '', short: str(c.short ?? c.description), category: str(c.category),
    kind: ['labor', 'part', 'fee', 'package'].includes(c.kind) ? c.kind : 'fee', default_minutes: num(c.default_minutes),
    price_cents: int(c.price_cents), price_max_cents: num(c.price_max_cents), cost_cents: num(c.cost_cents), skill: str(c.skill),
    taxable: c.taxable !== false, tech_payable: !!c.tech_payable, active: c.active !== false && !c.archived_at,
  };
}

// ─────────────────────────────────────────────────────────── jobs
export function apiLine(l: Row): JobLine {
  const kind = ['labor', 'part', 'fee', 'package'].includes(l.kind) ? l.kind : 'fee';
  const price = int(l.unit_price_cents);
  return {
    id: l.id, catalog_item_id: str(l.catalog_item_id), kind, description: l.description ?? '', qty: num(l.qty) ?? 1,
    unit_price_cents: l.kind === 'discount' ? -Math.abs(price) : price, unit_cost_cents: num(l.unit_cost_cents),
    taxable: l.taxable !== false, tech_payable: !!l.tech_payable, add_on: !!l.add_on,
  };
}

export function apiEstimate(e: Row | null | undefined, jobId: string): Estimate | null {
  if (!e) return null;
  return {
    id: e.id, job_id: e.job_id ?? jobId, version_no: int(e.version_no, 1), status: e.status ?? 'draft',
    subtotal_cents: int(e.subtotal_cents), tax_cents: int(e.tax_cents), total_cents: int(e.total_cents),
    sent_at: iso(e.sent_at), approved_at: iso(e.approved_at), approved_via: e.approved_via ?? null, lines: arr(e.lines).map(apiLine),
  };
}
/** The estimate the UI works on: newest version that isn't superseded. */
export const currentEstimate = (rows: Row[] | undefined): Row | null =>
  [...arr(rows)].filter((e) => e.status !== 'superseded').sort((a, b) => int(b.version_no) - int(a.version_no))[0] ?? null;

const HIDDEN_CANCEL = new Set(['CANCELLED - RAIN', 'CANCELLED - EV VEHICLE', 'CANCELLED - RSA']);
/**
 * P1 `allowed:{actions,statuses}` → the UI's `allowed_actions[]` vocabulary. Only a rename of what the server allows:
 * a status-backed button (cancel, no-show, duplicate, record cash) exists only when that status is in `statuses`.
 */
export function allowedToActions(job: Row): string[] {
  const acts = new Set<string>(arr<string>(job.allowed?.actions ?? job.allowed_actions));
  const sts = new Set<string>(arr<string>(job.allowed?.statuses));
  const out: string[] = [];
  const ph = phaseOf(job.order_status);
  // Closed jobs: only an owner override remains (P1 still lists DUPLICATE for them).
  if (isTerminal(job.order_status)) return acts.has('override_status') ? ['override_status'] : [];
  if (acts.has('assign')) out.push(...(job.assigned_tech_id ? ['reassign', 'unassign'] : ['assign']));
  if (job.order_status === 'AWAITING APPROVAL' && acts.has('send_estimate')) out.push('approve');
  if (acts.has('send_estimate')) out.push('send_estimate');
  if (acts.has('edit') && ph !== 'in_progress' && ph !== 'invoiced') out.push('reschedule');
  if (acts.has('cancel') && (CANCELLED_STATUSES as readonly string[]).some((s) => sts.has(s) && !HIDDEN_CANCEL.has(s))) out.push('cancel');
  if (ph === 'en_route' && sts.has('CANCELLED - CUSTOMER DID NOT RESPOND')) out.push('no_show');
  if (sts.has('DUPLICATE')) out.push('duplicate');
  if (ph === 'invoiced') out.push('pay_link');
  // P1 allows CASH TAKEN from most open statuses; the button only makes sense once work is done or invoiced.
  if (sts.has('CASH TAKEN') && (ph === 'in_progress' || ph === 'invoiced')) out.push('record_cash');
  if (acts.has('override_status')) out.push('override_status');
  if (acts.has('add_note') || acts.has('edit')) out.push('note');
  return out;
}

const addrLabel = (a: Row | null | undefined) => (a ? [a.line1, a.city].filter(Boolean).join(', ') || null : null);

/** A job from the list (`{items}` rows: customer_name/customer_phone/tech_* joined) or the detail (`{job}` + related). */
export function apiJob(j: Row): Job {
  const lines = arr(j.lines).map(apiLine);
  const status = ((ORDER_STATUSES as readonly string[]).includes(j.order_status) ? j.order_status : 'CONFIRMED') as OrderStatus;
  const est = j.estimate !== undefined ? j.estimate : currentEstimate(j.estimates);
  return {
    id: j.id, job_no: j.job_ref ?? (j.job_no != null ? `J-${j.job_no}` : ''), location_id: str(j.location_id), customer_id: j.customer_id ?? '',
    vehicle_id: str(j.vehicle_id), lead_id: str(j.lead_id),
    customer_name: j.customer?.display_name ?? j.customer_name ?? 'Customer', customer_phone_e164: str(j.customer?.phone_e164 ?? j.customer_phone),
    vehicle_label: vehicleLabel(j.vehicle) ?? str(j.vehicle_label),
    service_summary: j.service_type || lines.map((l) => l.description).slice(0, 2).join(', ') || 'Service visit',
    address_label: addrLabel(j.address) ?? str(j.address_label), lat: num(j.lat ?? j.address?.lat), lng: num(j.lng ?? j.address?.lng), zone: str(j.zone),
    order_status: status, status_changed_at: iso(j.status_changed_at) ?? iso(j.created_at) ?? '', booking_source: j.booking_source ?? 'staff',
    scheduled_start: iso(j.scheduled_start), scheduled_end: iso(j.scheduled_end), est_minutes: num(j.est_minutes),
    assigned_tech_id: str(j.assigned_tech_id), payment_status: j.payment_status ?? 'unpaid',
    total_cents: int(j.total_cents), paid_cents: int(j.paid_cents), currency: 'usd',
    customer_notes: str(j.customer_notes), internal_notes: str(j.internal_notes), created_at: iso(j.created_at) ?? '', completed_at: iso(j.completed_at),
    version: int(j.version, 1), allowed_actions: allowedToActions(j),
    lines, estimate: apiEstimate(est, j.id), photo_count: int(j.photo_count), part_count: int(j.part_count),
  };
}

const EVENT_TYPES = new Set(['status_changed', 'note', 'call', 'sms', 'assignment', 'estimate', 'payment', 'arrival', 'system']);
/** `/jobs/:id/timeline` row (job_events ∪ communications) → JobEvent. */
export function apiTimeline(r: Row, jobId: string, names?: Map<string, string>): JobEvent {
  const comm = r.kind === 'communication';
  const data = { ...(r.data ?? {}) } as Record<string, unknown>;
  let type: JobEvent['type'];
  if (comm) {
    type = r.channel === 'voice' ? 'call' : r.channel === 'sms' ? 'sms' : 'system';
    Object.assign(data, { body: r.body, direction: r.direction, status: r.status, channel: r.channel, template: r.type });
  } else {
    type = EVENT_TYPES.has(r.type) ? r.type : 'system';
    if (r.type === 'note' && typeof data.text === 'string') data.kind ??= 'note';
    if (r.type === 'assignment' && Array.isArray(data.released)) data.kind ??= 'unassigned';
    if (r.type === 'estimate' && data.approved) data.kind ??= 'approved';
  }
  return {
    id: `${comm ? 'c' : 'e'}${r.id}`, job_id: jobId, type, from_status: str(r.from_status), to_status: str(r.to_status),
    actor_kind: r.actor_kind ?? (comm ? 'system' : 'system'), actor_name: (r.actor_id && names?.get(String(r.actor_id))) ?? null,
    source: str(r.source), data, at: iso(r.at) ?? '',
  };
}

// ─────────────────────────────────────────────────────────── leads
const LEAD_SOURCES = ['call', 'sms', 'web', 'ai_receptionist', 'referral', 'fleet_portal'] as const;
/** `status_reason` is stored as `<disposition>[: note]` (P1 has no disposition column yet). */
export const dispositionFromReason = (reason: unknown) => (typeof reason === 'string' && reason ? reason.split(':')[0]!.trim() || null : null);

export function apiLead(l: Row, names?: Map<string, string>): Lead & { email?: string | null } {
  const d = (l.data ?? {}) as Row;
  const owner = str(l.owner ?? l.claimed_by);
  const claimer = [l.claimer_first_name, l.claimer_last_name].filter(Boolean).join(' ') || null;
  if (owner && claimer) names?.set(owner, claimer);
  const lease = iso(l.lease_expires_at);
  const live = owner && lease && Date.parse(lease) > Date.now();
  return {
    id: l.id, source: (LEAD_SOURCES as readonly string[]).includes(l.source) ? l.source : 'referral',
    phone_e164: str(l.phone_e164), name: str(l.name), need: str(l.need), vehicle_text: str(l.vehicle_text), location_id: str(l.location_id),
    status: l.status ?? 'new', disposition: str(d.disposition) ?? dispositionFromReason(d.status_reason),
    claimed_by: live ? owner : null, claimed_by_name: live ? claimer ?? names?.get(owner!) ?? null : null,
    claimed_at: live ? iso(l.claimed_at) : null, lease_expires_at: live ? lease : null, follow_up_due_at: iso(d.follow_up_due_at),
    ai_live: !!d.ai_live, job_id: str(l.job_id), customer_id: str(l.customer_id), call_id: str(d.call_id),
    summary: str(d.summary), intent: str(d.intent), quote_min_cents: num(d.quote_min_cents), quote_max_cents: num(d.quote_max_cents),
    conversation_count: int(l.conversation_count), created_at: iso(l.created_at) ?? '', last_contact_at: iso(l.last_contact_at),
    email: str(d.email),
    respond_by: iso(l.respond_by), overdue_at: iso(l.overdue_at), escalation_level: int(l.escalation_level),
    booking_request: d.booking_request && typeof d.booking_request === 'object' ? d.booking_request as BookingRequest : null,
  };
}

// ─────────────────────────────────────────────────────────── calls (P4 voice)
export function apiCall(c: Row, turns: Row[] = [], recordings: Row[] = []): Call {
  const d = (c.data ?? {}) as Row;
  const started = c.started_at ? Date.parse(c.started_at) : Date.now();
  const transcript: TranscriptTurn[] = [];
  for (const t of turns) {
    const offset = int(t.turn_no);
    if (t.asr_text) transcript.push({ speaker: 'caller', text: String(t.asr_text), offset });
    if (t.bot_text) transcript.push({ speaker: 'ai', text: String(t.bot_text), offset });
  }
  const recorded = !!c.has_recording || recordings.length > 0;
  return {
    id: c.id, direction: c.direction === 'outbound' ? 'outbound' : 'inbound', from_e164: c.from_e164 ?? '', to_e164: c.to_e164 ?? '',
    caller_name: str(d.caller_name), answered_by: c.route_action === 'ai' || c.kind === 'ai_inbound' ? 'ai' : c.answered_at ? 'staff' : null,
    state: c.ended_at ? 'ended' : c.answered_at ? 'live' : 'ringing', started_at: iso(c.started_at) ?? '',
    duration_sec: num(c.duration_sec) ?? (c.ended_at ? Math.max(0, Math.round((Date.parse(c.ended_at) - started) / 1000)) : 0),
    intent: str(d.intent ?? c.handoff_context?.intent), outcome: str(c.outcome), summary: str(d.summary ?? c.handoff_context?.summary),
    after_hours: !!d.after_hours, recording_id: recorded ? c.id : null, lead_id: str(c.lead_id), customer_id: str(c.customer_id),
    job_id: str(c.job_id), vehicle_id: null, transcript,
  };
}

/** GET /jobs/:id plus the current estimate's lines (the job read carries estimate headers only). */
export async function fetchJob(http: Http, id: string): Promise<Job> {
  const r = await http.get<{ job: Row }>(`${APP}/jobs/${encodeURIComponent(id)}`);
  const est = currentEstimate(r.job.estimates);
  const full = est ? await http.get<{ estimate: Row }>(`${APP}/estimates/${encodeURIComponent(est.id)}`).then((x) => x.estimate).catch(() => est) : null;
  return apiJob({ ...r.job, estimate: full });
}
